import React, { useCallback, useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, RotateCcw, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../services";

// ── Types ──────────────────────────────────────────────────────────────────────
type TxSource =
  | "order_payment"
  | "order_refund"
  | "gift_card_created"
  | "gift_card_redeemed"
  | "new_user_gift_card_redeemed"
  | "bonus_to_wallet_transfer"
  | "admin_top_up"
  | "stripe_payment"
  | "loyalty_earned"
  | "loyalty_reversed"
  | "compensation_reversed"
  | "birthday_reward";

interface WalletTxn {
  _id: string;
  amount: number;
  type: "credit" | "debit";
  walletType: "wallet" | "bonus_wallet";
  source: TxSource;
  description: string;
  balanceBefore: number;
  balanceAfter: number;
  orderId?: string;
  createdAt: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const SOURCE_LABELS: Record<TxSource, string> = {
  order_payment: "Order Payment",
  order_refund: "Order Refund",
  gift_card_created: "Gift Card Sent",
  gift_card_redeemed: "Gift Card Redeemed",
  new_user_gift_card_redeemed: "Welcome Gift",
  bonus_to_wallet_transfer: "Bonus Transfer",
  admin_top_up: "Wallet Top-up",
  stripe_payment: "Card Payment",
  loyalty_earned: "Reward Earned",
  loyalty_reversed: "Reward Reversed",
  birthday_reward: "Birthday Reward",
  compensation_reversed: "Compensation Reversed",
};

function formatTxDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }) + " · " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Transaction history of one wallet: the main wallet (Wallet page) or the
 * cash earned / bonus wallet (Rewards Program page).
 */
const TransactionHistory: React.FC<{ walletType: "wallet" | "bonus_wallet"; title?: string }> = ({
  walletType,
  title = "Transaction History",
}) => {
  const { user, currencySymbol } = useAuth();
  const [txns, setTxns] = useState<WalletTxn[]>([]);
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [txnsPage, setTxnsPage] = useState(1);
  const [txnsHasMore, setTxnsHasMore] = useState(true);

  const fetchTxns = useCallback(async (page: number, reset = false) => {
    try {
      setTxnsLoading(true);
      const params: Record<string, any> = { page, limit: 15, walletType };
      const res = await api.get("/wallet/transactions", { params });
      const incoming: WalletTxn[] = res.data.data || [];
      setTxns((prev) => reset ? incoming : [...prev, ...incoming]);
      setTxnsHasMore(page < (res.data.pages || 1));
    } catch {
      // silently fail — not critical
    } finally {
      setTxnsLoading(false);
    }
  }, [walletType]);

  useEffect(() => {
    if (user) {
      setTxnsPage(1);
      fetchTxns(1, true);
    }
  }, [user, fetchTxns]);

  const handleLoadMoreTxns = () => {
    const next = txnsPage + 1;
    setTxnsPage(next);
    fetchTxns(next, false);
  };

  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-black/5">
        <h3 className="font-bold text-[16px] sm:text-[18px] text-black">
          {title}
        </h3>
      </div>

      {/* List */}
      <ul className="divide-y divide-black/5 p-0">
        {txns.length === 0 && !txnsLoading && (
          <li className="px-5 py-10 text-center text-sm text-black/40">
            No transactions yet.
          </li>
        )}

        {txns.map((tx) => {
          const isCredit = tx.type === "credit";
          const isRefund = tx.source === "order_refund";

          return (
            <li key={tx._id} className="flex items-start gap-3 sm:gap-4 px-3 py-4 hover:bg-black/[0.02] transition">
              {/* Icon */}
              <div
                className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                  isRefund
                    ? "bg-blue-50"
                    : isCredit
                    ? "bg-green-50"
                    : "bg-red-50"
                }`}
              >
                {isRefund ? (
                  <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                ) : isCredit ? (
                  <ArrowDownLeft className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                ) : (
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                )}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[13px] sm:text-[14px] text-black truncate">
                    {/* Older entries were saved with the Loyalty wording */}
                    {(tx.description || SOURCE_LABELS[tx.source] || tx.source).replace(/^Loyalty\b/, "Reward")}
                  </span>
                  {/* Type badge */}
                  <span
                    className={`text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      isRefund
                        ? "bg-blue-100 text-blue-600"
                        : isCredit
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {isRefund ? "Refunded" : isCredit ? "Credited" : "Debited"}
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {/* Source chip */}
                  <span className="text-[10px] sm:text-[11px] text-black/50 bg-black/5 px-2 py-0.5 rounded-full">
                    {SOURCE_LABELS[tx.source] || tx.source}
                  </span>
                  {/* Wallet type chip */}
                  <span className="text-[10px] sm:text-[11px] text-black/50">
                    {tx.walletType === "wallet" ? "Main Wallet" : "Cash Earned"}
                  </span>
                </div>

                <div className="text-[10px] sm:text-[11px] text-black/40 mt-1">
                  {formatTxDate(tx.createdAt)}
                </div>
              </div>

              {/* Amount + balance */}
              <div className="text-right flex-shrink-0">
                <div
                  className={`font-bold text-[14px] sm:text-[15px] ${
                    isRefund
                      ? "text-blue-600"
                      : isCredit
                      ? "text-green-600"
                      : "text-red-500"
                  }`}
                >
                  {isCredit ? "+" : "−"}{currencySymbol}{(tx.amount ?? 0).toFixed(2)}
                </div>
                <div className="text-[10px] sm:text-[11px] text-black/40 mt-0.5">
                  Bal: {currencySymbol}{(tx.balanceAfter ?? 0).toFixed(2)}
                </div>
              </div>
            </li>
          );
        })}

        {/* Loading skeleton rows */}
        {txnsLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <li key={`sk-${i}`} className="flex items-center gap-4 px-5 py-4 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-black/5 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-black/5 rounded w-2/3" />
                <div className="h-2 bg-black/5 rounded w-1/3" />
              </div>
              <div className="h-4 bg-black/5 rounded w-14" />
            </li>
          ))}
      </ul>

      {/* Load more */}
      {txnsHasMore && !txnsLoading && txns.length > 0 && (
        <div className="px-5 py-4 border-t border-black/5">
          <button
            onClick={handleLoadMoreTxns}
            className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-black/60 hover:text-black transition py-1"
          >
            <ChevronDown className="w-4 h-4" />
            Load more
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
